import React, { useState, useRef } from 'react';
import { Camera, Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { AppUser } from '../../types';
import { uploadSpotImage } from '../../services/firebaseService';
import { compressImage } from '../../utils/imageCompression';
import { emitRewardEvent } from '../../services/rewardEvents';

interface SpotImageUploadProps {
  spotId: string;
  spotName?: string;
  // true = plassen mangler ekte brukerbilde — opplastingen utløser førstemann-bonus.
  isFirstPhoto?: boolean;
  user: AppUser | null;
  onOpenAuth: () => void;
}

const SpotImageUpload: React.FC<SpotImageUploadProps> = ({ spotId, spotName, isFirstPhoto, user, onOpenAuth }) => {
  const { t } = useTranslation();
  const [isUploading, setIsUploading] = useState(false);
  const [preview, setPreview] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!user) { onOpenAuth(); return; }

    setIsUploading(true);
    try {
      const compressed = await compressImage(file);
      setPreview(compressed.previewUrl);

      await uploadSpotImage(
        spotId,
        user.uid,
        user.displayName || t('firebase.anonymous', 'Anonym'),
        compressed.file
      );

      URL.revokeObjectURL(compressed.previewUrl);
      setPreview(null);

      emitRewardEvent({
        kind: 'add_photo',
        title: spotName || t('spot.unnamed', 'Ukjent plass'),
        message: isFirstPhoto
          ? 'Du la inn det aller første bildet av plassen — nå blir den langt mer attraktiv for andre.'
          : 'Bildet gjør plassen lettere å vurdere for andre brukere.',
        rewardPoints: isFirstPhoto ? 50 : 15,
      });
    } catch (error) {
      console.error('Image upload failed:', error);
    } finally {
      setIsUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  return (
    <>
      <button
        onClick={(e) => { e.stopPropagation(); user ? fileInputRef.current?.click() : onOpenAuth(); }}
        disabled={isUploading}
        className="p-1 transition-all active:scale-125 text-[var(--text-muted)] hover:text-[var(--text-secondary)] disabled:opacity-50"
        title={isUploading ? t('spot_images.uploading', 'Laster opp...') : t('spot_images.upload_photo', 'Last opp bilde')}
        aria-label={isUploading ? t('spot_images.uploading', 'Laster opp...') : t('spot_images.upload_photo', 'Last opp bilde')}
      >
        {isUploading ? <Loader2 size={16} className="animate-spin" /> : <Camera size={16} />}
      </button>
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={handleFileSelect}
      />
      {preview && (
        <div className="mt-2 relative w-16 h-16 rounded-xl overflow-hidden border border-emerald-500/30">
          <img src={preview} className="w-full h-full object-cover" alt="" />
          <div className="absolute inset-0 flex items-center justify-center bg-black/40">
            <Loader2 size={16} className="animate-spin text-white" />
          </div>
        </div>
      )}
    </>
  );
};

export default SpotImageUpload;
